/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/platform-admin/AdminManagement.tsx
 * PURPOSE: Admin user management interface with NQH Brand Design System
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: Multi-tenant Platform Administration, Vietnamese F&B
 * - Impact: Platform Critical - Platform admin management
 * - Stakeholders: Platform Super Admins, Nhat Quang Holding, IT Team
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Components
 * - Pattern: React, CSS Modules, NQH Brand Design System
 * - Dependencies: React 18, React Router, TypeScript, CSS Modules
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - API: docs/03-integrate/01-API-Specifications/
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePlatformAdminAuth } from '../../contexts/PlatformAdminContext';
import {
  platformAdminApi,
  PlatformAdminResponse,
  PlatformAdminCreate,
  ResetPlatformAdminPasswordRequest,
  ResetPlatformAdminPasswordResponse,
  AccountLockoutStatusResponse,
  UnlockAccountRequest,
} from '../../services/api/platform-admin/platformAdmin';
import styles from './AdminManagement.module.css';

export const AdminManagement: React.FC = () => {
  const { platformAdmin, isAuthenticated, isInitialized } = usePlatformAdminAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState<PlatformAdminResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [newAdminData, setNewAdminData] = useState<PlatformAdminCreate>({
    email: '',
    username: '',
    password: '',
    full_name: '',
    role: 'platform_operator',
    notes: '',
  });

  // Reset Password Modal State (ADR-027 Bflow Alignment)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordAdmin, setResetPasswordAdmin] = useState<PlatformAdminResponse | null>(null);
  const [resetPasswordMode, setResetPasswordMode] = useState<'generate_temporary' | 'set_temporary'>('generate_temporary');
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [resetReason, setResetReason] = useState('');
  const [resetTicketId, setResetTicketId] = useState('');
  const [forceChangeOnLogin, setForceChangeOnLogin] = useState(true);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<ResetPlatformAdminPasswordResponse | null>(null);

  // Sprint 28: Account Lockout State (ADR-039 Track A)
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockAdmin, setUnlockAdmin] = useState<PlatformAdminResponse | null>(null);
  const [unlockLockoutStatus, setUnlockLockoutStatus] = useState<AccountLockoutStatusResponse | null>(null);
  const [unlockReason, setUnlockReason] = useState('');
  const [unlockTicketId, setUnlockTicketId] = useState('');
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [unlockSuccess, setUnlockSuccess] = useState(false);
  const [lockoutStatuses, setLockoutStatuses] = useState<Record<string, AccountLockoutStatusResponse>>({});

  // Redirect to PA login if not authenticated after initialization
  useEffect(() => {
    if (isInitialized && !isAuthenticated) {
      navigate('/platform-admin/login');
    }
  }, [isInitialized, isAuthenticated, navigate]);

  useEffect(() => {
    loadAdmins();
  }, [platformAdmin.accessToken]);

  const loadAdmins = async () => {
    if (!platformAdmin.accessToken) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const adminList = await platformAdminApi.listAdmins(platformAdmin.accessToken);
      setAdmins(adminList);
    } catch (err: any) {
      setError(err.message || 'Failed to load administrators');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!platformAdmin.accessToken) return;

    try {
      setIsCreating(true);
      setError(null);

      const createdAdmin = await platformAdminApi.createAdmin(platformAdmin.accessToken, newAdminData);

      // Add to list
      setAdmins(prev => [createdAdmin, ...prev]);

      // Reset form
      setNewAdminData({
        email: '',
        username: '',
        password: '',
        full_name: '',
        role: 'platform_operator',
        notes: '',
      });

      setShowCreateModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to create administrator');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeactivateAdmin = async (adminId: string) => {
    if (!platformAdmin.accessToken) return;
    if (!confirm('Are you sure you want to deactivate this administrator?')) return;

    try {
      await platformAdminApi.deactivateAdmin(platformAdmin.accessToken, adminId);

      // Update local state
      setAdmins(prev => prev.map(admin =>
        admin.id === adminId ? { ...admin, is_active: false } : admin
      ));
    } catch (err: any) {
      setError(err.message || 'Failed to deactivate administrator');
    }
  };

  // Open Reset Password Modal (ADR-027 Bflow Alignment)
  const openResetPasswordModal = (admin: PlatformAdminResponse) => {
    setResetPasswordAdmin(admin);
    setResetPasswordMode('generate_temporary');
    setTemporaryPassword('');
    setResetReason('');
    setResetTicketId('');
    setForceChangeOnLogin(true);
    setResetPasswordError(null);
    setResetPasswordSuccess(null);
    setShowResetPasswordModal(true);
  };

  // Close Reset Password Modal
  const closeResetPasswordModal = () => {
    setShowResetPasswordModal(false);
    setResetPasswordAdmin(null);
    setResetPasswordError(null);
    setResetPasswordSuccess(null);
  };

  // Password validation: 12+ chars, 1 number, 1 uppercase, 1 lowercase, 1 special
  const validatePassword = (password: string): string | null => {
    if (password.length < 12) {
      return 'Password must be at least 12 characters';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least 1 number';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least 1 uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least 1 lowercase letter';
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return 'Password must contain at least 1 special character';
    }
    return null;
  };

  // Handle Reset Password Submission (ADR-027 Bflow Alignment)
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!platformAdmin.accessToken || !resetPasswordAdmin) return;

    // Validate reason (CTO REQUIRED: min 5 characters)
    if (resetReason.length < 5) {
      setResetPasswordError('Reason must be at least 5 characters (audit requirement)');
      return;
    }

    // Validate password if mode is 'set_temporary'
    if (resetPasswordMode === 'set_temporary') {
      const pwdError = validatePassword(temporaryPassword);
      if (pwdError) {
        setResetPasswordError(pwdError);
        return;
      }
    }

    try {
      setIsResettingPassword(true);
      setResetPasswordError(null);

      const resetData: ResetPlatformAdminPasswordRequest = {
        mode: resetPasswordMode,
        force_change_on_next_login: forceChangeOnLogin,
        reason: resetReason,
        ticket_id: resetTicketId || undefined,
      };

      if (resetPasswordMode === 'set_temporary') {
        resetData.temporary_password = temporaryPassword;
      }

      const result = await platformAdminApi.resetPlatformAdminPassword(
        platformAdmin.accessToken,
        resetPasswordAdmin.id,
        resetData
      );

      setResetPasswordSuccess(result);

      // Update local state to reflect force_change_on_next_login
      setAdmins(prev => prev.map(admin =>
        admin.id === resetPasswordAdmin.id
          ? { ...admin, force_change_on_next_login: forceChangeOnLogin }
          : admin
      ));

    } catch (err: any) {
      setResetPasswordError(err.message || 'Failed to reset password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Check if current user can reset passwords (must be platform_super_admin or platform_operator)
  const canResetPasswords = (): boolean => {
    const role = platformAdmin.admin?.role;
    return role === 'platform_super_admin' || role === 'platform_operator';
  };

  // Sprint 28: Check if current user can unlock accounts (SECURITY_ADMIN or SUPER_ADMIN)
  const canUnlockAccounts = (): boolean => {
    const role = platformAdmin.admin?.role;
    return role === 'platform_super_admin' || role === 'platform_security';
  };

  // Sprint 28: Load lockout status for all admins
  const loadLockoutStatuses = async () => {
    if (!platformAdmin.accessToken || !canUnlockAccounts()) return;

    const statuses: Record<string, AccountLockoutStatusResponse> = {};
    for (const admin of admins) {
      try {
        const status = await platformAdminApi.getAccountLockoutStatus(
          platformAdmin.accessToken,
          admin.id
        );
        statuses[admin.id] = status;
      } catch {
        // Ignore errors for individual status checks
      }
    }
    setLockoutStatuses(statuses);
  };

  // Sprint 28: Load lockout statuses when admins list changes
  useEffect(() => {
    if (admins.length > 0 && canUnlockAccounts()) {
      loadLockoutStatuses();
    }
  }, [admins]);

  // Sprint 28: Open Unlock Modal
  const openUnlockModal = async (admin: PlatformAdminResponse) => {
    if (!platformAdmin.accessToken) return;

    setUnlockAdmin(admin);
    setUnlockReason('');
    setUnlockTicketId('');
    setUnlockError(null);
    setUnlockSuccess(false);
    setShowUnlockModal(true);

    try {
      const status = await platformAdminApi.getAccountLockoutStatus(
        platformAdmin.accessToken,
        admin.id
      );
      setUnlockLockoutStatus(status);
    } catch (err: any) {
      setUnlockError(err.message || 'Failed to load lockout status');
    }
  };

  // Sprint 28: Close Unlock Modal
  const closeUnlockModal = () => {
    setShowUnlockModal(false);
    setUnlockAdmin(null);
    setUnlockLockoutStatus(null);
    setUnlockError(null);
    setUnlockSuccess(false);
  };

  // Sprint 28: Handle Unlock Account
  const handleUnlockAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!platformAdmin.accessToken || !unlockAdmin) return;

    if (unlockReason.length < 5) {
      setUnlockError('Reason must be at least 5 characters (audit requirement)');
      return;
    }

    try {
      setIsUnlocking(true);
      setUnlockError(null);

      const unlockData: UnlockAccountRequest = {
        reason: unlockReason,
        ticket_id: unlockTicketId || undefined,
      };

      await platformAdminApi.unlockAdminAccount(
        platformAdmin.accessToken,
        unlockAdmin.id,
        unlockData
      );

      setUnlockSuccess(true);

      // Update local lockout status
      setLockoutStatuses(prev => ({
        ...prev,
        [unlockAdmin.id]: {
          ...prev[unlockAdmin.id],
          is_locked: false,
          failed_login_attempts: 0,
          locked_until: null,
          lockout_reason: null,
        },
      }));

      // Close modal after short delay
      setTimeout(() => {
        closeUnlockModal();
      }, 2000);

    } catch (err: any) {
      setUnlockError(err.message || 'Failed to unlock account');
    } finally {
      setIsUnlocking(false);
    }
  };

  // Sprint 28: Check if admin is locked
  const isAdminLocked = (adminId: string): boolean => {
    const status = lockoutStatuses[adminId];
    return status?.is_locked ?? false;
  };

  // Sprint 28: Get lockout info for display
  const getLockoutInfo = (adminId: string): string | null => {
    const status = lockoutStatuses[adminId];
    if (!status?.is_locked) return null;

    if (status.locked_until) {
      const lockedUntil = new Date(status.locked_until);
      return `Khóa đến ${lockedUntil.toLocaleTimeString()}`;
    }
    return 'Tài khoản đã khóa';
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContainer}>
          <div className={styles.headerContent}>
            <div>
              <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <ol className={styles.breadcrumbList}>
                  <li>
                    <Link to="/platform-admin/dashboard" className={styles.breadcrumbLink}>
                      Bảng điều khiển
                    </Link>
                  </li>
                  <li>
                    <span className={styles.breadcrumbSeparator}>/</span>
                  </li>
                  <li>
                    <span className={styles.breadcrumbCurrent}>Quản lý quản trị viên</span>
                  </li>
                </ol>
              </nav>
              <h1 className={styles.pageTitle}>Quản trị viên nền tảng</h1>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className={styles.primaryButton}
            >
              Tạo quản trị viên mới
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className={styles.main}>
        {error && (
          <div className={styles.errorAlert}>
            {error}
            <button
              onClick={() => setError(null)}
              className={styles.errorDismiss}
            >
              ×
            </button>
          </div>
        )}

        {/* Admins Table */}
        <div className={styles.card}>
          <div className={styles.cardContent}>
            <h3 className={styles.cardTitle}>
              Quản trị viên nền tảng ({admins.length})
            </h3>

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead className={styles.tableHead}>
                  <tr>
                    <th className={styles.tableHeader}>
                      Thông tin quản trị
                    </th>
                    <th className={styles.tableHeader}>
                      Vai trò
                    </th>
                    <th className={styles.tableHeader}>
                      Trạng thái
                    </th>
                    <th className={styles.tableHeader}>
                      Đăng nhập lần cuối
                    </th>
                    <th className={styles.tableHeader}>
                      Hành động
                    </th>
                  </tr>
                </thead>
                <tbody className={styles.tableBody}>
                  {admins.map((admin) => (
                    <tr key={admin.id} className={styles.tableRow}>
                      <td className={styles.tableCell}>
                        <div>
                          <div className={styles.adminName}>{admin.full_name}</div>
                          <div className={styles.adminEmail}>{admin.email}</div>
                          <div className={styles.adminUsername}>@{admin.username}</div>
                        </div>
                      </td>
                      <td className={styles.tableCell}>
                        <span className={`${styles.badge} ${
                          admin.role === 'platform_super_admin'
                            ? styles.badgeSuperAdmin
                            : styles.badgeOperator
                        }`}>
                          {admin.role.replace('platform_', '').replace('_', ' ').toUpperCase()}
                        </span>
                      </td>
                      <td className={styles.tableCell}>
                        <div className={styles.statusContainer}>
                          <span className={`${styles.badge} ${
                            admin.is_active
                              ? styles.badgeActive
                              : styles.badgeInactive
                          }`}>
                            {admin.is_active ? 'Hoạt động' : 'Không hoạt động'}
                          </span>
                          {admin.is_verified && (
                            <span className={`${styles.badge} ${styles.badgeVerified}`}>
                              Đã xác minh
                            </span>
                          )}
                          {admin.mfa_enabled && (
                            <span className={`${styles.badge} ${styles.badgeMfa}`}>
                              MFA
                            </span>
                          )}
                          {/* Sprint 28: Account Lockout Badge */}
                          {isAdminLocked(admin.id) && (
                            <span
                              className={`${styles.badge} ${styles.badgeLocked}`}
                              title={getLockoutInfo(admin.id) || 'Tài khoản đã khóa'}
                            >
                              🔒 Đã khóa
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={styles.tableCell}>
                        <span className={styles.dateCell}>
                          {admin.last_login
                            ? new Date(admin.last_login).toLocaleDateString()
                            : 'Chưa bao giờ'
                          }
                        </span>
                      </td>
                      <td className={styles.tableCell}>
                        <div className={styles.actionsContainer}>
                          {/* Sprint 28: Unlock Account Button - Only for SECURITY_ADMIN when account is locked */}
                          {canUnlockAccounts() && isAdminLocked(admin.id) && admin.id !== platformAdmin.admin?.id && (
                            <button
                              onClick={() => openUnlockModal(admin)}
                              className={`${styles.actionButton} ${styles.actionUnlock}`}
                              title="Mở khóa tài khoản quản trị viên"
                            >
                              Mở khóa tài khoản
                            </button>
                          )}
                          {/* Reset Password Button - Only for PLATFORM_OPERATOR, not self */}
                          {canResetPasswords() && admin.id !== platformAdmin.admin?.id && admin.is_active && (
                            <button
                              onClick={() => openResetPasswordModal(admin)}
                              className={`${styles.actionButton} ${styles.actionResetPassword}`}
                              title="Đặt lại mật khẩu quản trị viên"
                            >
                              Đặt lại mật khẩu
                            </button>
                          )}
                          {admin.is_active && admin.id !== platformAdmin.admin?.id && (
                            <button
                              onClick={() => handleDeactivateAdmin(admin.id)}
                              className={`${styles.actionButton} ${styles.actionDeactivate}`}
                            >
                              Vô hiệu hóa
                            </button>
                          )}
                          {admin.id === platformAdmin.admin?.id && (
                            <span className={styles.currentUser}>Người dùng hiện tại</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Tạo quản trị viên nền tảng mới</h3>

            <form onSubmit={handleCreateAdmin} className={styles.form}>
              <div className={styles.formGroup}>
                <label>Họ và tên</label>
                <input
                  type="text"
                  required
                  value={newAdminData.full_name}
                  onChange={(e) => setNewAdminData(prev => ({ ...prev, full_name: e.target.value }))}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Email</label>
                <input
                  type="email"
                  required
                  value={newAdminData.email}
                  onChange={(e) => setNewAdminData(prev => ({ ...prev, email: e.target.value }))}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Tên đăng nhập</label>
                <input
                  type="text"
                  required
                  value={newAdminData.username}
                  onChange={(e) => setNewAdminData(prev => ({ ...prev, username: e.target.value }))}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Mật khẩu</label>
                <input
                  type="password"
                  required
                  value={newAdminData.password}
                  onChange={(e) => setNewAdminData(prev => ({ ...prev, password: e.target.value }))}
                  className={styles.formInput}
                />
              </div>

              <div className={styles.formGroup}>
                <label>Vai trò</label>
                <select
                  value={newAdminData.role}
                  onChange={(e) => setNewAdminData(prev => ({ ...prev, role: e.target.value }))}
                  className={styles.formSelect}
                >
                  <option value="platform_operator">Vận hành nền tảng</option>
                  <option value="platform_support_l1">Hỗ trợ L1</option>
                  <option value="platform_support_l2">Hỗ trợ L2</option>
                  <option value="platform_security">Quản trị bảo mật</option>
                  <option value="platform_devops">Quản trị DevOps</option>
                  <option value="platform_super_admin">Siêu quản trị</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label>Ghi chú</label>
                <textarea
                  value={newAdminData.notes}
                  onChange={(e) => setNewAdminData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className={styles.formTextarea}
                />
              </div>

              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className={styles.cancelButton}
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className={styles.primaryButton}
                >
                  {isCreating ? 'Đang tạo...' : 'Tạo quản trị viên'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal (ADR-027 Bflow Alignment) */}
      {showResetPasswordModal && resetPasswordAdmin && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Đặt lại mật khẩu quản trị viên</h3>

            {/* Admin Info */}
            <div className={styles.resetPasswordInfo}>
              <p><strong>Quản trị viên:</strong> {resetPasswordAdmin.full_name}</p>
              <p><strong>Email:</strong> {resetPasswordAdmin.email}</p>
              <p><strong>Vai trò:</strong> {resetPasswordAdmin.role.replace('platform_', '').replace('_', ' ').toUpperCase()}</p>
            </div>

            {/* Warning Banner */}
            <div className={styles.warningAlert}>
              <strong>⚠️ Cảnh báo:</strong> Hành động này sẽ:
              <ul>
                <li>Đặt lại mật khẩu quản trị viên</li>
                <li>Thu hồi TẤT CẢ phiên hoạt động (buộc đăng nhập lại)</li>
                <li>Tạo bản ghi kiểm toán</li>
              </ul>
            </div>

            {/* Success Message */}
            {resetPasswordSuccess && (
              <div className={styles.successAlert}>
                <p><strong>✓ Đặt lại mật khẩu thành công!</strong></p>
                {resetPasswordSuccess.temporary_password && (
                  <div className={styles.generatedPasswordContainer}>
                    <p>Mật khẩu được tạo:</p>
                    <code className={styles.generatedPassword}>
                      {resetPasswordSuccess.temporary_password}
                    </code>
                    <p className={styles.passwordNote}>
                      Vui lòng sao chép mật khẩu ngay. Mật khẩu sẽ không hiển thị lại.
                    </p>
                  </div>
                )}
                <p>Phiên đã thu hồi: {resetPasswordSuccess.sessions_revoked}</p>
              </div>
            )}

            {/* Error Message */}
            {resetPasswordError && (
              <div className={styles.errorAlertInline}>
                {resetPasswordError}
              </div>
            )}

            {/* Form - Only show if not yet successful */}
            {!resetPasswordSuccess && (
              <form onSubmit={handleResetPassword} className={styles.form}>
                {/* Mode Selector */}
                <div className={styles.formGroup}>
                  <label>Chế độ mật khẩu *</label>
                  <select
                    value={resetPasswordMode}
                    onChange={(e) => setResetPasswordMode(e.target.value as 'generate_temporary' | 'set_temporary')}
                    className={styles.formSelect}
                  >
                    <option value="generate_temporary">Tạo mật khẩu an toàn (Khuyến nghị)</option>
                    <option value="set_temporary">Đặt mật khẩu tùy chỉnh</option>
                  </select>
                </div>

                {/* Custom Password Input (only for set_temporary mode) */}
                {resetPasswordMode === 'set_temporary' && (
                  <div className={styles.formGroup}>
                    <label>Mật khẩu tạm thời *</label>
                    <input
                      type="password"
                      required
                      minLength={12}
                      value={temporaryPassword}
                      onChange={(e) => {
                        setTemporaryPassword(e.target.value);
                        setResetPasswordError(null);
                      }}
                      className={styles.formInput}
                      placeholder="Tối thiểu 12 ký tự, 1 số, 1 chữ hoa, 1 chữ thường, 1 ký tự đặc biệt"
                    />
                  </div>
                )}

                {/* Reason (REQUIRED per CTO mandate) */}
                <div className={styles.formGroup}>
                  <label>Lý do * (Yêu cầu kiểm toán)</label>
                  <textarea
                    required
                    minLength={5}
                    maxLength={200}
                    value={resetReason}
                    onChange={(e) => {
                      setResetReason(e.target.value);
                      setResetPasswordError(null);
                    }}
                    className={styles.formTextarea}
                    placeholder="Nhập lý do đặt lại mật khẩu (tối thiểu 5 ký tự)"
                    rows={2}
                  />
                </div>

                {/* Ticket ID (Recommended) */}
                <div className={styles.formGroup}>
                  <label>Mã phiếu (Khuyến nghị)</label>
                  <input
                    type="text"
                    value={resetTicketId}
                    onChange={(e) => setResetTicketId(e.target.value)}
                    className={styles.formInput}
                    placeholder="VD: INC-1234, JIRA-567"
                  />
                </div>

                {/* Force Change on Next Login */}
                <div className={styles.formGroupCheckbox}>
                  <label>
                    <input
                      type="checkbox"
                      checked={forceChangeOnLogin}
                      onChange={(e) => setForceChangeOnLogin(e.target.checked)}
                      className={styles.formCheckbox}
                    />
                    Buộc đổi mật khẩu khi đăng nhập lần tới
                  </label>
                </div>

                <div className={styles.modalActions}>
                  <button
                    type="button"
                    onClick={closeResetPasswordModal}
                    className={styles.cancelButton}
                    disabled={isResettingPassword}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    disabled={isResettingPassword}
                    className={`${styles.primaryButton} ${styles.dangerButton}`}
                  >
                    {isResettingPassword ? 'Đang đặt lại...' : 'Đặt lại mật khẩu'}
                  </button>
                </div>
              </form>
            )}

            {/* Close Button (after success) */}
            {resetPasswordSuccess && (
              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={closeResetPasswordModal}
                  className={styles.primaryButton}
                >
                  Đóng
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Sprint 28: Unlock Account Modal (ADR-039 Track A) */}
      {showUnlockModal && unlockAdmin && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Mở khóa tài khoản quản trị viên</h3>

            {/* Admin Info */}
            <div className={styles.resetPasswordInfo}>
              <p><strong>Quản trị viên:</strong> {unlockAdmin.full_name}</p>
              <p><strong>Email:</strong> {unlockAdmin.email}</p>
              <p><strong>Vai trò:</strong> {unlockAdmin.role.replace('platform_', '').replace('_', ' ').toUpperCase()}</p>
            </div>

            {/* Lockout Status Info */}
            {unlockLockoutStatus && (
              <div className={styles.warningAlert}>
                <strong>🔒 Chi tiết khóa tài khoản:</strong>
                <ul>
                  <li>Số lần đăng nhập thất bại: {unlockLockoutStatus.failed_login_attempts}</li>
                  {unlockLockoutStatus.locked_until && (
                    <li>Khóa đến: {new Date(unlockLockoutStatus.locked_until).toLocaleString()}</li>
                  )}
                  {unlockLockoutStatus.lockout_reason && (
                    <li>Lý do khóa: {unlockLockoutStatus.lockout_reason}</li>
                  )}
                  {unlockLockoutStatus.last_failed_login && (
                    <li>Lần đăng nhập thất bại cuối: {new Date(unlockLockoutStatus.last_failed_login).toLocaleString()}</li>
                  )}
                </ul>
              </div>
            )}

            {/* Success Message */}
            {unlockSuccess && (
              <div className={styles.successAlert}>
                <p><strong>✓ Mở khóa tài khoản thành công!</strong></p>
                <p>Quản trị viên có thể thử đăng nhập lại.</p>
              </div>
            )}

            {/* Error Message */}
            {unlockError && (
              <div className={styles.errorAlertInline}>
                {unlockError}
              </div>
            )}

            {/* Form - Only show if not yet successful */}
            {!unlockSuccess && (
              <form onSubmit={handleUnlockAccount} className={styles.form}>
                {/* Reason (REQUIRED per CTO mandate) */}
                <div className={styles.formGroup}>
                  <label>Lý do * (Yêu cầu kiểm toán)</label>
                  <textarea
                    required
                    minLength={5}
                    maxLength={200}
                    value={unlockReason}
                    onChange={(e) => {
                      setUnlockReason(e.target.value);
                      setUnlockError(null);
                    }}
                    className={styles.formTextarea}
                    placeholder="Nhập lý do mở khóa tài khoản (tối thiểu 5 ký tự)"
                    rows={2}
                  />
                </div>

                {/* Ticket ID (Recommended) */}
                <div className={styles.formGroup}>
                  <label>Mã phiếu (Khuyến nghị)</label>
                  <input
                    type="text"
                    value={unlockTicketId}
                    onChange={(e) => setUnlockTicketId(e.target.value)}
                    className={styles.formInput}
                    placeholder="VD: INC-1234, JIRA-567"
                  />
                </div>

                <div className={styles.modalActions}>
                  <button
                    type="button"
                    onClick={closeUnlockModal}
                    className={styles.cancelButton}
                    disabled={isUnlocking}
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    disabled={isUnlocking}
                    className={styles.primaryButton}
                  >
                    {isUnlocking ? 'Đang mở khóa...' : 'Mở khóa'}
                  </button>
                </div>
              </form>
            )}

            {/* Close Button (after success) */}
            {unlockSuccess && (
              <div className={styles.modalActions}>
                <button
                  type="button"
                  onClick={closeUnlockModal}
                  className={styles.primaryButton}
                >
                  Đóng
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;
